import { THREADS_GROUP } from "@kwp/firestore";
import {
  collectionGroup,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClients } from "../../lib/clients";
import { mergePages, toThread, type Thread } from "./model";
import type { Constraint } from "./scope";

export const PAGE_SIZE = 50;

/**
 * Firestore ends a listener that fails (a token refused after sleep, the network gone), so the
 * list would freeze until a reload. It subscribes again after this long, doubling per failure in
 * a row, up to a minute; a snapshot resets it.
 */
export function resubscribeDelayMs(failures: number): number {
  return Math.min(60_000, 2_000 * 2 ** Math.max(0, failures - 1));
}

export type ThreadsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; threads: Thread[]; hasMore: boolean; loadingMore: boolean };

function toFirestore(c: Constraint): QueryConstraint {
  if ("orderBy" in c) return orderBy(c.orderBy, c.direction);
  return where(c.field, c.op, c.asDate === true ? new Date(c.value as number) : c.value);
}

interface Page {
  threads: Thread[];
  last: QueryDocumentSnapshot | null;
  full: boolean;
}

/**
 * The unified inbox, live (architecture §8): one listener per page of 50. Page n+1 starts after
 * the last document page n had when "load more" was asked, so pages are cursor-paginated and each
 * stays live; merged by id, newest first. Changing the constraints starts again at one page.
 * null: not known yet (loading); "none": a scope with no accounts, empty without a query.
 */
export function useThreads(constraints: readonly Constraint[] | "none" | null): ThreadsState & { loadMore: () => void } {
  const { firebase } = useClients();
  const none = constraints === "none";
  const key = constraints === null || none ? null : JSON.stringify(constraints);
  const [cursors, setCursors] = useState<QueryDocumentSnapshot[]>([]);
  const [pages, setPages] = useState<Array<Page | null>>([null]);
  const [error, setError] = useState<string | null>(null);
  // Bumped to subscribe again after an error; `failures` counts errors since the last snapshot.
  const [generation, setGeneration] = useState(0);
  const failures = useRef(0);

  useEffect(() => {
    setCursors([]);
    setPages([null]);
    setError(null);
    failures.current = 0;
  }, [key]);

  useEffect(() => {
    if (error === null) return undefined;
    const timer = setTimeout(() => {
      setError(null);
      setGeneration((g) => g + 1);
    }, resubscribeDelayMs(failures.current));
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (key === null) return undefined;
    const base = (JSON.parse(key) as Constraint[]).map(toFirestore);
    const starts: Array<QueryDocumentSnapshot | null> = [null, ...cursors];
    const unsubscribes = starts.map((start, i) =>
      onSnapshot(
        query(collectionGroup(firebase.db, THREADS_GROUP), ...base, ...(start === null ? [] : [startAfter(start)]), limit(PAGE_SIZE)),
        (snap) => {
          const threads = snap.docs.flatMap((d) => {
            const t = toThread(d.id, d.data());
            return t === null ? [] : [t];
          });
          const last = snap.docs[snap.docs.length - 1];
          failures.current = 0;
          setError(null);
          setPages((all) => {
            const next = [...all];
            next[i] = { threads, last: last === undefined ? null : last, full: snap.size >= PAGE_SIZE };
            return next;
          });
        },
        (e) => {
          failures.current += 1;
          setError(e.message);
        },
      ),
    );
    return () => unsubscribes.forEach((u) => u());
  }, [firebase.db, key, cursors, generation]);

  const loadMore = useCallback(() => {
    const lastPage = pages[pages.length - 1];
    if (lastPage === null || lastPage === undefined || lastPage.last === null || !lastPage.full) return;
    const cursor = lastPage.last;
    setCursors((c) => [...c, cursor]);
    setPages((p) => [...p, null]);
  }, [pages]);

  const state = useMemo<ThreadsState>(() => {
    if (none) return { status: "ready", threads: [], hasMore: false, loadingMore: false };
    const first = pages[0];
    // While it subscribes again, the list it had stays on screen; only a list never loaded errors.
    if (first === null || first === undefined) return error === null ? { status: "loading" } : { status: "error", message: error };
    const loaded = pages.filter((p): p is Page => p !== null);
    const tail = pages[pages.length - 1];
    return {
      status: "ready",
      threads: mergePages(loaded.map((p) => p.threads)),
      hasMore: tail !== null && tail !== undefined && tail.full,
      loadingMore: tail === null,
    };
  }, [pages, error, none]);

  return { ...state, loadMore };
}
